<script setup lang="ts">
import type { Components } from '@ionic/core'
import type { FormErrors } from 'vee-validate'
import type { AssociationMembershipResponse } from '~~/openapi-client/gen/schemas'
import type { ReactiveMember } from '~~/types/ReactiveMember'

const props = defineProps<{
	member: AssociationMembershipResponse
	nav: ComponentPublicInstance<Components.IonNav>
}>()

const nav = props.nav

const { canIEditEmail, canIEditMember } = useUserMembership()
const { refresh, isBoardMember } = useMembers()

let memberFields: ReactiveMember = {} as ReactiveMember
let resetForm: () => void
let errors: ComputedRef<FormErrors<Partial<Record<string, string | undefined>>>>
let meta: { value: { dirty: boolean } }
let init: (id: string) => Promise<void>
let update: (id: string) => Promise<Promise<void> | undefined>

if (isBoardMember(props.member)) {
	const form = useAssociationBoardMembershipForm()
	memberFields = form.memberFields
	resetForm = form.resetForm
	errors = form.errors
	meta = form.meta
	init = form.init
	update = form.update
} else {
	const form = useAssociationMembershipForm()
	memberFields = form.memberFields
	resetForm = form.resetForm
	errors = form.errors
	meta = form.meta
	init = form.init
	update = form.update
}
await init(props.member.id)

const handleSubmit = () => handleAsync(async () => {
	await update(props.member.id)
	await refresh()
	if (await nav.canGoBack()) {
		nav.pop()
	} else {
		await modalController.dismiss()
	}
	resetForm()
}, 'Fehler beim Aktualisieren des Mitglieds')

const isSubmitDisabled = computed(() =>
	!memberFields.firstname.value?.trim()
	|| !memberFields.lastname.value?.trim()
	|| !meta.value.dirty
	|| !meta.value.valid,
)
</script>

<template>
	<ModalContent
		title="Mitglied bearbeiten"
		show-back-button
	>
		<MemberForm
			v-model="memberFields"
			:errors="errors"
			:readonly="!canIEditMember(member)"
			:email-readonly="!canIEditEmail(member)"
			@cancel="resetForm"
		/>
		<template #actions>
			<VCIButton
				icon="mdi-content-save"
				:disabled="isSubmitDisabled"
				@click="handleSubmit"
			>
				Speichern
			</VCIButton>
		</template>
	</ModalContent>
</template>
